define((require) => {
    "use strict";

    var GroupModel       = require("shared/ui/groups/GroupModel"),
        Preconditions    = require("shared/util/Preconditions"),
        RestApi          = require("shared/services/RestApi"),
        Syncer           = require("shared/services/sync/Syncer"),
        SyncManager      = require("shared/services/sync/SyncManager"),
        Permissions      = require("shared/security/Permissions"),
        {Logger}         = require("shared/util/Logger"),
        SystemMessages   = require("editor/services/SystemMessages"),
        PublishableMixin = require("util/PublishableMixin");

    var log = new Logger("GroupModel");

    return GroupModel.mixin("GroupModelEditorMixin", {

        mixin: PublishableMixin,

        relations: GroupModel.prototype.relations,

        transient: ["resolvedAssets"],

        constructor: function(attributes, options) {
            this.__super();

            if (options && options.pushedOnly) {
                // To avoid ending up in the same SyncManager queue (when using multiple
                // instances of *this* group) create a unique id for this synced target.
                Syncer.State.init(this, {
                    unique: function() {
                        return ["pushed_only_" + this.id];
                    }.bind(this)
                });
            }
        },

        /**
         * A configuration which specify which fields of the model needs to be updated on model sync event.
         * This is called by the ImprovedModel. We should not call this method anywhere.
         *
         * @param {Object} options Passed in by the improvedModel
         * @returns {String[]}  An array of properties needs updating
         */
        // Update on sync, and PublishableMixin will ensure publish related attributes are updated...
        updateOnSync: function(options) {
            if (options.sync === "save") {
                return ["companyId"];
            }
        },

        /**
         * Attaches the specified asset and it's reference.
         *
         * @param {*} asset The asset, e.g. <tt>ExperienceModel</tt>.
         * @param {*} value The (optional) value associated with the asset in this group.
         *
         * @return {*|Promise}
         */
        attach: function(asset, value) {
            Preconditions.instanceOf(asset, this._assetClass);

            if (value) {
                Preconditions.instanceOf(value, this._valueClass);
            }

            this.get("entries").attach(asset, value, this._valueType);

            return this.save();
        },

        /**
         * Removes the entry (or entries) by the specified asset and/or value.<p>
         *
         * If no value is specified, all the entries represented by the asset are removed.
         *
         * @param  {ImprovedModel} asset The asset.
         * @param  {*}             value The optional value.
         *
         * @return {Promise}       A promise representing the save operation.
         */
        detach: function(asset, value) {
            Preconditions.instanceOf(asset, this._assetClass);

            this.get("entries").detach(asset, value);

            return this.save();
        },

        destroyWithAssets: function() {
            return this.destroy({url: this.url() + "/assets"});
        },

        fetchPushedOnly: function() {
            const url = this.url();
            if (url) {
                return this.fetch({url: url + "/pushed"});
            }

            return Promise.resolve(false);
        },

        /**
         * Pushes this group model, optionally including all its assets (e.g. Experiences) with non-pushed
         * changes. <p>
         *
         * If <tt>withAssets</tt> is true, all updated assets (Experiences) already cached on the frontend
         * will be manually updated based on the returned json.
         *
         * @param {Boolean} [withAssets] True to push this group and its assets with non pushed changes,
         *                               false to only push this group it self (default).
         * @return {Promise} A promise that resolves once the push has completed; never falsey.
         */
        push: function(withAssets) {
            // Create sync options...
            var options = {
                immutable: true,
                method: withAssets ? "push-with-assets" : "push", // different name, different semantics

                // Make sure we only push non-erroneous, non-destroyed experiences...
                validate: function() { // Invoked with this model (target) as scope
                    var error = null;

                    if (this._destroyed) {
                        error = "destroyed";
                    } else if (this.syncState.actual.state === Syncer.State.Failed) {
                        error = "unsaved";
                    }

                    if (error) {
                        return "Cannot push " + error + " group";
                    }
                    // no error
                },
                // Make sure user can publish!
                privilege: Permissions.Privileges.PUBLISH,
                promise:   true
            };

            // Assets need special handling...
            if (withAssets) {
                return this._pushWithAssets(options);
            }

            // Group only...
            return this._execute(options, {
                url:  RestApi.pathToCollectionOnResource(RestApi.Collections.GROUPS, this.get("id"), "push"),
                type: "PUT"
            }).promise;
        },

        _pushWithAssets: function(options) {
            // If we are pushing with assets, we get an array back of all pushed assets, including the group it self
            // if pushed, and not just the group itself. If we are just pushing the group, we only get the group
            // back, always even if not pushed. In either case, the model group model will already have been updated
            // accordingly (via syncer) when our result handler is called, so we only need to handle the additional
            // assets (for now Experiences) returned as well.
            //
            // Since we only submit the group alone, we have to block all its assets (Experiences) at the frontend
            // while the push is pending, and we cannot start the push with assets while the assets have pending
            // sync requests or we can get concurrent modification errors. This way, concurrent sync requests for an
            // asset will be queued until we have our response which may include auxiliary assets to update. Each
            // asset is stored as a reference, and we only need to block if we have globally cached version of the
            // asset (only Experiences for now)...

            var updatedAssets = {}, // since invoked on group result for different queue, we need to keep a reference to it...
                groupUpdated  = 0,
                assetsUpdated = 0,
                experiences = [],
                maxPurgeTime = null,
                wait;

            // For now, we assume all assets are experiences, but this may have to change in the future...
            this.getAssets().forEach((experience) => {
                experiences.push(experience);
            });

            // "done" hook to update the maximum purge time for any Group/Experience (asset)...
            var purgeTime = function(model) {
                maxPurgeTime = model.getPurgeTime(maxPurgeTime);
            };

            // Do we need to block at least one?
            if (experiences.length) {
                // Invariant: called before a "sync:state:synced" is raised for the group and before the "unblock"
                // function is called...
                options.resultHandler = function(assets) { // (empty) array of json data
                    var asset,
                        n = assets.length,
                        i;

                    for (i = 0; i < n; i++) {
                        asset = assets[i];
                        updatedAssets[asset.id] = asset; // json
                    }
                    groupUpdated  = updatedAssets[this.id] ? 1 : 0; // this Group
                    assetsUpdated = groupUpdated ? n - 1 : n;

                    // A sync result handler is always scoped for its target, here Group, so make sure we return our
                    // Group to get the purge time...
                    return this;
                };

                // Block the target queue for this group while there are pending sync requests for each Experience
                // used as an asset, then do the push with asset for the group, and only then unblock the experiences.
                // The actual push with asset for this group is enqueued below, and only after that do we call the
                // "wait function" to start the overall waiting/flow...
                wait = SyncManager.await(this, experiences, {
                    method: options.method,
                    fn: function() {
                        // "this" is the current experience, so we can easily update it now via a nested sync
                        // request if it was updated. If the push with asset did not return the specific experience
                        // as an asset, it was not updated because of the push and we simply do nothing...
                        var json = updatedAssets[this.get("id")];

                        if (json) {
                            log.info("Updating Experience pushed as asset:", this, json);

                            // Note: "options" will have been updated at call time to reflect the actual sync options
                            // used (copied, not reused as is)! Also, the update is a nested sync request, so it will
                            // be allowed to execute immediately...
                            this.update(json, options).done(purgeTime);
                        } else {
                            log.info("Experience not updated as asset:", this);
                        }
                    }
                });
            }

            // Enqueue request to do the push with assets of this group...
            var request = this._execute(options, {
                url:  RestApi.pathToCollectionOnResource(RestApi.Collections.GROUPS, this.get("id"), "assets/push"),
                type: "PUT"
            });

            // Remember that the group it self will also have a purge time...
            request.promise = request.promise.done(purgeTime);

            // Make sure we use the (defaulted) options when updating assets, e.g. with proper "sync" property...
            options = request.options;

            // Since target queue for this group is now primed with the actual push with asset request - after the block
            // request added via "SyncManager.await" above - we are ready to start the overall waiting logic...
            var promise = wait ? wait() : request.promise;

            // "promise" is now the promise that will resolve once this overall push-with-asset operation resolves,
            // either only for the group itself (i.e. no assets), but most like after the group has been updated *and*
            // all auxiliary assets as well. That means that on done we are ready to notify the user about the purge
            // time!...
            return promise.done(() => {
                if (maxPurgeTime !== null) {
                    var text = groupUpdated ? "Group" : "";
                    if (assetsUpdated) {
                        if (groupUpdated) {
                            text += " and ";
                        }
                        if (assetsUpdated > 1) {
                            text += assetsUpdated + " Experiences";
                        } else {
                            text += "Experience";
                        }
                    }
                    SystemMessages.notifyPurge(text, "pushed", maxPurgeTime, false);
                }
            });
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/groups/GroupModelEditorMixin.js